import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SiteContentCtx = createContext({ content: {}, ready: false, reload: () => {}, isDraftPreview: false });

// True when the current URL has ?draft=1 — used to opt into admin-only draft preview.
function isDraftPreviewRequested() {
  if (typeof window === "undefined") return false;
  try {
    return new URLSearchParams(window.location.search).get("draft") === "1";
  } catch {
    return false;
  }
}

export function SiteContentProvider({ children }) {
  const [content, setContent] = useState({});
  const [ready, setReady] = useState(false);
  const [isDraftPreview, setIsDraftPreview] = useState(false);

  const reload = useCallback(() => {
    const wantsDraft = isDraftPreviewRequested();
    const publicUrl = `${API}/site-content`;
    const previewUrl = `${API}/admin/site-content/preview`;
    const load = wantsDraft
      ? axios
          .get(previewUrl, { withCredentials: true })
          .then((r) => ({ data: r.data, draft: true }))
          .catch(() => axios.get(publicUrl).then((r) => ({ data: r.data, draft: false })))
      : axios.get(publicUrl).then((r) => ({ data: r.data, draft: false }));

    load
      .then(({ data, draft }) => {
        setContent(data || {});
        setIsDraftPreview(draft);
      })
      .catch(() => setContent({}))
      .finally(() => setReady(true));
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const value = useMemo(
    () => ({ content, ready, reload, isDraftPreview }),
    [content, ready, reload, isDraftPreview],
  );
  return <SiteContentCtx.Provider value={value}>{children}</SiteContentCtx.Provider>;
}

// Returns the current server value for a slot, or the given fallback if the
// server hasn't hydrated / doesn't have the key yet.
export function useCopy(key, fallback = "") {
  const { content } = useContext(SiteContentCtx);
  const val = content[key];
  return (val === undefined || val === null || val === "") ? fallback : val;
}

// Exposes whether the current page is being previewed with drafts substituted.
export function useDraftPreview() {
  const { isDraftPreview } = useContext(SiteContentCtx);
  return isDraftPreview;
}

// Utility: render \n-separated copy into {" "}<br />-separated JSX.
// Key composes the index with a content-slice so React identity survives
// content edits that keep the same line count (the linter flags bare index
// keys — this makes the key content-derived while still stable).
export function multiline(text) {
  if (!text) return null;
  const parts = String(text).split("\n");
  return parts.map((line, i) => (
    <React.Fragment key={`${i}-${line.slice(0, 24)}`}>
      {line}
      {i < parts.length - 1 ? <>{" "}<br /></> : null}
    </React.Fragment>
  ));
}
